import { useState } from 'react';
import Marquee from 'react-fast-marquee';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Reveal, SectionHead, VoltButton, Overline } from './motion';
import { useLang } from '../lib/lang';
import { CONTACT } from '../content/constants';

export const MarqueeStrip = ({ items }) => (
  <div data-testid="editorial-marquee" className="border-y border-white/10 bg-surface/60 py-5 overflow-hidden">
    <Marquee speed={32} gradient={false} pauseOnHover>
      {items.map((item, i) => (
        <span key={i} className="flex items-center font-mono text-xs md:text-sm uppercase tracking-[0.3em] text-zinc-400">
          <span className="px-8">{item}</span>
          <span className="text-volt" aria-hidden="true">///</span>
        </span>
      ))}
    </Marquee>
  </div>
);

export const SkeletonGrid = ({ items, cols = 'md:grid-cols-2', numbered = false, testId }) => (
  <div data-testid={testId} className={`grid ${cols} border-t border-l border-white/10`}>
    {items.map((item, i) => (
      <Reveal key={i} delay={i * 0.06} className={`group border-r border-b border-white/10 p-8 md:p-10 hover:bg-surface transition-colors duration-500`}>
        {numbered && (
          <span className="font-mono text-xs text-volt tracking-[0.2em]">{item.n || `0${i + 1}`}</span>
        )}
        <h3 className={`${numbered ? 'mt-4' : ''} font-display text-xl md:text-2xl font-bold tracking-tight text-white group-hover:text-volt transition-colors duration-500`}>
          {item.t || item.title}
        </h3>
        <p className="mt-4 text-sm text-zinc-400 leading-relaxed">{item.d || item.body}</p>
      </Reveal>
    ))}
  </div>
);

export const CTASection = ({ title, body, btn }) => {
  const { link } = useLang();
  return (
    <section data-testid="cta-section" className="relative border-t border-white/10 overflow-hidden">
      <div className="absolute inset-0 spotlight" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36">
        <Reveal>
          <h2 className="font-display text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] text-white max-w-3xl uppercase">
            {title}
          </h2>
          {body && <p className="mt-6 max-w-xl text-base text-zinc-400 leading-relaxed">{body}</p>}
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
            <VoltButton to={link('contact')} testId="cta-contact-button">{btn}</VoltButton>
            <div className="font-mono text-xs text-zinc-500 tracking-wider">
              <a href={`mailto:${CONTACT.email}`} data-testid="cta-email" className="hover:text-volt transition-colors duration-300">{CONTACT.email}</a>
              <span className="mx-3 text-zinc-700">·</span>
              <a href={`tel:${CONTACT.phoneHref}`} data-testid="cta-phone" className="hover:text-volt transition-colors duration-300">{CONTACT.phone}</a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const LinkTile = ({ to, title, body, linkLabel, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group block border-r border-b border-white/10 p-8 md:p-12 hover:bg-surface transition-colors duration-500"
  >
    <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white group-hover:text-volt transition-colors duration-500">
      {title}
    </h3>
    <p className="mt-5 text-sm text-zinc-400 leading-relaxed">{body}</p>
    <span className="mt-8 inline-flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-volt">
      {linkLabel}
      <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
    </span>
  </Link>
);

export const PartnerLogo = ({ slug, name, className = '' }) => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        data-testid={`partner-logo-${slug}`}
        className={`flex items-center justify-center bg-surface border border-white/10 ${className}`}
      >
        <span className="font-display font-black uppercase tracking-tight text-white text-base md:text-lg px-4 text-center leading-tight">
          {name}
        </span>
      </div>
    );
  }
  return (
    <img
      src={`/partners/${slug}-tile.png`}
      alt={`${name} logo`}
      data-testid={`partner-logo-${slug}`}
      onError={() => setFailed(true)}
      className={`object-cover border border-white/10 ${className}`}
    />
  );
};

export const PageSection = ({ overline, title, children, className = '', testId }) => (
  <section data-testid={testId} className={`border-t border-white/10 ${className}`}>
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
      {(overline || title) && <SectionHead overline={overline} title={title} className="mb-12 md:mb-16" />}
      {children}
    </div>
  </section>
);

export { Overline, SectionHead };
